import { twMerge } from "tailwind-merge";
import { Button } from "../../../components/Button";
import { OnboardingImage } from "./OnboardingImage";
import { OnboardingCard } from "./types";

/**
 * Pastel palette mirroring the five tiers (Recruit → PraxisPro). Pill labels are
 * data-driven, so colours are assigned by position and cycle if a card ever
 * ships more pills than the palette holds.
 */
const PILL_COLORS = [
    { bg: "#FEF9C3" }, // yellow
    { bg: "#DCFCE7" }, // green
    { bg: "#DBEAFE" }, // blue
    { bg: "#EDE9FE" }, // purple
    { bg: "#FFEDD5" }, // orange
];

function Pill({ label, index, className }: { label: string; index: number; className?: string }) {
    const color = PILL_COLORS[index % PILL_COLORS.length];
    return (
        <span
            className={twMerge(
                "inline-flex items-center border-[1.135px] border-gray-201 justify-center rounded-full text-black-2 px-4 py-2 text-base font-medium whitespace-nowrap",
                className,
            )}
            style={{ backgroundColor: color.bg }}
        >
            {label}
        </span>
    );
}

export function OnboardingCardView({
    card,
    onCtaClick,
}: {
    card: OnboardingCard;
    onCtaClick: () => void;
}) {
    const hasImage = !!card.image_url;
    const hasPills = !!card.pills?.length;
    // When an illustration is present it owns the right column and pills sit under
    // the copy as a wrapping row. Otherwise the pills become the right-side visual
    // and stack vertically (e.g. the tiers slide).
    const pillsAreVisual = hasPills && !hasImage;

    return (
        <div className="w-full h-full flex flex-col md:flex-row gap-6 md:gap-8 items-stretch">
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex-1 flex flex-col justify-center gap-4">
                    <h2 className="text-2xl leading-tight font-semibold text-black">{card.title}</h2>

                    <div
                        className="text-base leading-relaxed text-black [&_b]:font-semibold"
                        dangerouslySetInnerHTML={{ __html: card.description }}
                    />

                    {hasPills && !pillsAreVisual && (
                        <div className="flex flex-wrap gap-2">
                            {card.pills!.map((label, index) => (
                                <Pill key={label} label={label} index={index} />
                            ))}
                        </div>
                    )}
                </div>

                <div className="pt-2">
                    <Button
                        variant="primary"
                        className="!h-11 !rounded-full text-base !bg-primary !px-8"
                        onClick={onCtaClick}
                    >
                        {card.cta}
                    </Button>
                </div>
            </div>

            {(hasImage || pillsAreVisual) && (
                <div className="w-[290px] flex items-center justify-center">
                    {hasImage ? (
                        <OnboardingImage
                            imageUrl={card.image_url}
                            alt={card.title}
                            className="w-full max-w-[288px] max-h-[280px]"
                        />
                    ) : (
                        <div className="flex w-full max-w-[320px] flex-col gap-3">
                            {card.pills!.map((label, index) => (
                                <Pill key={label} label={label} index={index} className="w-full py-3 text-[15px]" />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
